import { useMemo, useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  getFirestore
} from 'firebase/firestore';
import firebaseApp from '../../../firebase/credenciales';

const db = getFirestore(firebaseApp);

/**
 * Hook personalizado para calcular estadísticas y datos de gráficos de planillas
 * @param {Array} planillas - Lista de planillas
 * @param {Object} usuario - Datos del usuario actual
 * @returns {Object} - Estadísticas y datos para gráficos
 */
export const usePlanillasAnalytics = (planillas, usuario) => {
  const [detallesActuales, setDetallesActuales] = useState([]);
  const [ocurrenciasMensuales, setOcurrenciasMensuales] = useState([]);
  const [loadingDetalles, setLoadingDetalles] = useState(false);

  useEffect(() => {
    const cargarDatosUlimaPlanilla = async () => {
      if (planillas.length === 0 || !usuario?.ruc) {
        setDetallesActuales([]);
        setOcurrenciasMensuales([]);
        return;
      }

      const ultimaPlanilla = planillas[0];
      setLoadingDetalles(true);
      try {
        const qDetalles = query(
          collection(db, "pl_planilla_detalle"),
          where("ruc_empresa", "==", usuario.ruc),
          where("periodo", "==", ultimaPlanilla.periodo)
        );
        const snapDetalles = await getDocs(qDetalles);
        const dataDetalles = snapDetalles.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setDetallesActuales(dataDetalles);

        const qOcurrencias = query(
          collection(db, "pl_ocurrencias"),
          where("ruc_empresa", "==", usuario.ruc),
          where("periodo", "==", ultimaPlanilla.periodo)
        );
        const snapOcurrencias = await getDocs(qOcurrencias);
        const dataOcurrencias = snapOcurrencias.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setOcurrenciasMensuales(dataOcurrencias);

      } catch (err) {
        console.error("Error cargando datos para analytics:", err);
      } finally {
        setLoadingDetalles(false);
      }
    };

    cargarDatosUlimaPlanilla();
  }, [planillas, usuario?.ruc]);

  const estadisticas = useMemo(() => {
    const actual = planillas[0] || {};
    const anterior = planillas[1] || {};
    const gastoActual = actual.costo_laboral_total || actual.total_neto || 0;
    const gastoAnterior = anterior.costo_laboral_total || anterior.total_neto || 0;
    const variacionGasto = gastoAnterior > 0 ? ((gastoActual - gastoAnterior) / gastoAnterior) * 100 : 0;
    const hcActual = actual.total_empleados || 0;
    const hcAnterior = anterior.total_empleados || 0;
    const variacionHC = hcAnterior > 0 ? hcActual - hcAnterior : 0;
    const costoPromedio = hcActual > 0 ? gastoActual / hcActual : 0;
    const totalDiasVacaciones = detallesActuales.reduce((sum, d) => sum + (Number(d.dias_vacaciones) || 0), 0);
    const totalDiasFaltas = detallesActuales.reduce((sum, d) => sum + (Number(d.dias_faltantes) || 0), 0);
    
    // NUEVO: Análisis de Horas Extras para KPI
    const totalCostoHE = detallesActuales.reduce((sum, d) => {
      const he25 = Number(d.ingresos?.horas_extras_25) || 0;
      const he35 = Number(d.ingresos?.horas_extras_35) || 0;
      const he100 = Number(d.ingresos?.horas_extras_100) || 0;
      return sum + he25 + he35 + he100;
    }, 0);

    const totalHorasHE = detallesActuales.reduce((sum, d) => {
      const h25 = Number(d.he_25) || 0;
      const h35 = Number(d.he_35) || 0;
      const h100 = Number(d.he_100) || 0;
      return sum + h25 + h35 + h100;
    }, 0);

    const ajustesManuales = detallesActuales.filter(d => (d.faltas_injustificadas || 0) > 0 || (d.he_25 || 0) > 0 || (d.he_35 || 0) > 0 || (d.he_100 || 0) > 0 || (d.descuentos?.adelantos || 0) > 0 || (d.vacaciones || 0) > 0).length;
    const ratioError = hcActual > 0 ? (ajustesManuales / hcActual) * 100 : 0;
    
    return { 
      total: planillas.length, 
      borradores: planillas.filter((p) => p.estado === "BORRADOR").length, 
      procesadas: planillas.filter((p) => p.estado === "PROCESADO").length, 
      aprobadas: planillas.filter((p) => p.estado === "APROBADO").length, 
      totalEmpleados: hcActual, 
      totalNeto: actual.total_neto || 0, 
      gastoActual, 
      gastoAnterior, 
      variacionGasto, 
      hcActual, 
      hcAnterior, 
      variacionHC, 
      costoPromedio, 
      ratioError, 
      totalDiasVacaciones, 
      totalDiasFaltas,
      totalCostoHE,
      totalHorasHE
    };
  }, [planillas, detallesActuales]);

  const datosContratos = useMemo(() => {
    if (detallesActuales.length === 0) return [];
    const contratoMap = {};
    detallesActuales.forEach(d => {
      const tipo = d.tipo_contrato || "Sin Especificar";
      if (!contratoMap[tipo]) contratoMap[tipo] = { name: tipo, value: 0 };
      contratoMap[tipo].value += 1;
    });
    return Object.values(contratoMap).sort((a, b) => b.value - a.value);
  }, [detallesActuales]);

  const datosGraficoEstados = useMemo(() => {
    return [{ name: "Borradores", value: estadisticas.borradores, color: "#ffc107" }, { name: "Procesadas", value: estadisticas.procesadas, color: "#fd7e14" }, { name: "Aprobadas", value: estadisticas.aprobadas, color: "#8B4513" }];
  }, [estadisticas]);

  const datosGraficoTendencias = useMemo(() => {
    const tendenciasData = [];
    const mesesArr = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
    const añoActual = new Date().getFullYear();
    
    for (let mesIdx = 0; mesIdx < 12; mesIdx++) {
      const periodoKey = `${añoActual}-${String(mesIdx + 1).padStart(2, "0")}`;
      const planillasMes = planillas.filter(p => p.periodo === periodoKey);
      
      const totalNeto = planillasMes.reduce((sum, p) => sum + (p.total_neto || 0), 0);
      const costoTotal = planillasMes.reduce((sum, p) => sum + (p.costo_laboral_total || p.total_neto || 0), 0);
      const totalEmpleados = planillasMes.reduce((sum, p) => sum + (p.total_empleados || 0), 0);

      tendenciasData.push({
        mes: mesesArr[mesIdx],
        fullMes: `${mesesArr[mesIdx]} ${añoActual}`,
        planillas: planillasMes.length,
        totalNeto: totalNeto,
        costoTotal: costoTotal,
        empleados: totalEmpleados
      });
    }
    return tendenciasData;
  }, [planillas]);

  const datosGraficoTipos = useMemo(() => {
    const tiposCount = {};
    planillas.forEach(planilla => {
      const tipo = planilla.tipo_planilla || 'MENSUAL';
      tiposCount[tipo] = (tiposCount[tipo] || 0) + 1;
    });
    return Object.entries(tiposCount).map(([tipo, cantidad]) => ({ name: tipo, value: cantidad, color: tipo === 'MENSUAL' ? '#007bff' : tipo === 'GRATIFICACION' ? '#28a745' : tipo === 'CTS' ? '#dc3545' : '#6c757d' }));
  }, [planillas]);

  const datosDesgloseCostos = useMemo(() => {
    if (detallesActuales.length === 0) return [];
    const desglose = {
      sueldoBase: 0,
      horasExtras: 0,
      bonificaciones: 0,
      aportes: 0,
      vacaciones: 0,
      provisiones: 0,
      otros: 0
    };

    detallesActuales.forEach(d => {
      // 1. Total Ingresos (Sueldo, AF, HE, Bonos, Vacaciones Gozadas, Grati/CTS pagada)
      const ingresosBrutos = (d.total_ingresos_bruto || 0);
      desglose.sueldoBase += ingresosBrutos;

      // 2. Total Aportes Empleador (Essalud, SCTR, etc.)
      desglose.aportes += (d.total_aportaciones || 0);

      // 3. Total Provisiones (Grati, CTS, Vacaciones devengadas)
      const montoProvisiones = d.provisiones?.total || d.total_provisiones || 0;
      desglose.provisiones += montoProvisiones;
    });

    return [
      { name: "Total Ingresos", value: desglose.sueldoBase, color: "#0d6efd" },
      { name: "Total Aportaciones", value: desglose.aportes, color: "#fd7e14" },
      { name: "Total Provisiones", value: desglose.provisiones, color: "#20c997" }
    ].filter(i => i.value > 0);
  }, [detallesActuales]);

  const datosDepartamentos = useMemo(() => {
    if (detallesActuales.length === 0) return [];
    const deptoMap = {};
    detallesActuales.forEach(d => {
      const depto = d.departamento || d.area || "General";
      if (!deptoMap[depto]) deptoMap[depto] = { name: depto, total: 0, count: 0 };
      deptoMap[depto].total += (d.costo_laboral_total || d.total_ingresos_bruto || 0);
      deptoMap[depto].count += 1;
    });
    return Object.values(deptoMap).sort((a, b) => b.total - a.total);
  }, [detallesActuales]);

  const datosHE = useMemo(() => {
    if (detallesActuales.length === 0) return null;
    const he = { h25: { name: "HE 25%", horas: 0, monto: 0 }, h35: { name: "HE 35%", horas: 0, monto: 0 }, h100: { name: "HE 100%", horas: 0, monto: 0 } };
    detallesActuales.forEach(d => {
      he.h25.horas += Number(d.he_25 || 0);
      he.h25.monto += Number(d.ingresos?.horas_extras_25 || 0);
      he.h35.horas += Number(d.he_35 || 0);
      he.h35.monto += Number(d.ingresos?.horas_extras_35 || 0);
      he.h100.horas += Number(d.he_100 || 0);
      he.h100.monto += Number(d.ingresos?.horas_extras_100 || 0);
    });
    return Object.values(he);
  }, [detallesActuales]);

  const datosAusentismo = useMemo(() => {
    if (detallesActuales.length === 0) return null;
    const aus = { vacaciones: { name: "Vacaciones", dias: 0, monto: 0, color: "#BC8F8F" }, faltas: { name: "Faltas Injust.", dias: 0, monto: 0, color: "#dc3545" }, permisos_sg: { name: "Permisos S/G", dias: 0, monto: 0, color: "#6c757d" }, permisos_cg: { name: "Permisos C/G", dias: 0, monto: 0, color: "#28a745" } };
    detallesActuales.forEach(d => {
      const dailyRate = (Number(d.sueldo_base || 0) + Number(d.asignacion_familiar || 0)) / 30;
      aus.vacaciones.dias += Number(d.dias_vacaciones || 0);
      aus.vacaciones.monto += Number(d.vacaciones || 0);
      const diasFaltas = Number(d.dias_faltantes || 0);
      aus.faltas.dias += diasFaltas;
      aus.faltas.monto += d.monto_faltas ? Number(d.monto_faltas) : (dailyRate * diasFaltas);
      const diasSG = Number(d.permisos_sin_goce || 0);
      aus.permisos_sg.dias += diasSG;
      aus.permisos_sg.monto += d.monto_permisos_sin_goce ? Number(d.monto_permisos_sin_goce) : (dailyRate * diasSG);
      
      // Permisos Con Goce: DEDUPLICACIÓN PARA REGISTROS ANTIGUOS
      let diasCG = Number(d.permisos_con_goce || 0);
      const diasVac = Number(d.dias_vacaciones || 0);
      // Si el sistema anterior guardó vacaciones como permisos_con_goce (bug previo), restamos
      if (diasCG > 0 && diasVac > 0 && Math.abs(diasCG - diasVac) < 0.1) {
          diasCG = 0; // Se asume que CG = Vacaciones
      } else if (diasCG > diasVac && diasVac > 0) {
          diasCG = diasCG - diasVac; // Se asume que Vacaciones está incluido en el total
      }
      
      aus.permisos_cg.dias += diasCG;
      aus.permisos_cg.monto += d.monto_permisos_con_goce ? Number(d.monto_permisos_con_goce) : (dailyRate * diasCG);
    });
    return Object.values(aus);
  }, [detallesActuales]);

  const datosPulsoDiario = useMemo(() => {
    if (ocurrenciasMensuales.length === 0) return [];
    const pulso = Array.from({ length: 31 }, (_, i) => ({ dia: i + 1, faltas: 0, he: 0, vacaciones: 0 }));
    ocurrenciasMensuales.forEach(o => {
      const fechaParts = o.fecha?.split("-");
      if (!fechaParts) return;
      const diaNum = parseInt(fechaParts[2], 10);
      if (diaNum >= 1 && diaNum <= 31) {
        const index = diaNum - 1;
        if (o.tipo === "falta") pulso[index].faltas += Number(o.dias_afectados || 1);
        else if (o.tipo?.startsWith("he")) pulso[index].he += Number(o.horas_afectadas || 0);
        else if (o.tipo === "vacaciones") pulso[index].vacaciones += Number(o.dias_afectados || 1);
      }
    });
    return pulso;
  }, [ocurrenciasMensuales]);

  return { estadisticas, datosGraficoEstados, datosGraficoTendencias, datosGraficoTipos, datosDesgloseCostos, datosDepartamentos, datosContratos, datosHE, datosAusentismo, datosPulsoDiario, loadingDetalles };
};